import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const DraftTendenciesChart = ({ selectedManager, draftData = [], className = "" }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadAndProcessData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!draftData || draftData.length === 0) {
          throw new Error('No draft data available');
        }

        if (!selectedManager) {
          throw new Error('No manager selected');
        }

        // Process draft data to calculate spending tendencies
        // This logic should match the R Shiny app's st dataframe creation
        
        // Group data by Owner and Year to calculate yearly spending by position
        const ownerYearData = {};
        
        draftData.forEach(row => {
          const owner = row.Manager || row.Owner;
          const year = row.Year;
          const position = (row.Position || '').toUpperCase();
          const cost = parseFloat(row.Cost || 0);
          
          if (!owner || !year || !position || isNaN(cost)) return;
          
          const key = `${owner}_${year}`;
          if (!ownerYearData[key]) {
            ownerYearData[key] = {
              Owner: owner,
              Year: year,
              QBCost: 0, RBCost: 0, WRCost: 0, TECost: 0, KCost: 0, DCost: 0,
              TotalCost: 0
            };
          }
          
          // Map positions to cost fields
          switch(position) {
            case 'QB':
              ownerYearData[key].QBCost += cost;
              break;
            case 'RB':
              ownerYearData[key].RBCost += cost;
              break;
            case 'WR':
              ownerYearData[key].WRCost += cost;
              break;
            case 'TE':
              ownerYearData[key].TECost += cost;
              break;
            case 'K':
              ownerYearData[key].KCost += cost;
              break;
            case 'DEF':
            case 'DST':
              ownerYearData[key].DCost += cost;
              break;
          }
          
          ownerYearData[key].TotalCost += cost;
        });

        // Convert to array and calculate percentages
        const stData = Object.values(ownerYearData).map(item => ({
          ...item,
          QBPctBudget: item.TotalCost > 0 ? item.QBCost / item.TotalCost : 0,
          RBPctBudget: item.TotalCost > 0 ? item.RBCost / item.TotalCost : 0,
          WRPctBudget: item.TotalCost > 0 ? item.WRCost / item.TotalCost : 0,
          TEPctBudget: item.TotalCost > 0 ? item.TECost / item.TotalCost : 0,
          KPctBudget: item.TotalCost > 0 ? item.KCost / item.TotalCost : 0,
          DPctBudget: item.TotalCost > 0 ? item.DCost / item.TotalCost : 0
        }));

        if (!stData.length) {
          throw new Error('No processed spending data available');
        }

        // Calculate league averages (medians)
        const calculateMedian = (values) => {
          const sorted = values.filter(v => !isNaN(v)).sort((a, b) => a - b);
          if (sorted.length === 0) return 0;
          const mid = Math.floor(sorted.length / 2);
          return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
        };

        const qbAvg = calculateMedian(stData.map(row => row.QBPctBudget));
        const rbAvg = calculateMedian(stData.map(row => row.RBPctBudget));
        const wrAvg = calculateMedian(stData.map(row => row.WRPctBudget));
        const teAvg = calculateMedian(stData.map(row => row.TEPctBudget));
        const kAvg = calculateMedian(stData.map(row => row.KPctBudget));
        const defAvg = calculateMedian(stData.map(row => row.DPctBudget));

        // Calculate manager's medians
        const managerData = stData.filter(row => row.Owner === selectedManager);
        
        if (!managerData.length) {
          throw new Error(`No data found for manager: ${selectedManager}`);
        }

        const manQb = calculateMedian(managerData.map(row => row.QBPctBudget));
        const manRb = calculateMedian(managerData.map(row => row.RBPctBudget));
        const manWr = calculateMedian(managerData.map(row => row.WRPctBudget));
        const manTe = calculateMedian(managerData.map(row => row.TEPctBudget));
        const manK = calculateMedian(managerData.map(row => row.KPctBudget));
        const manDef = calculateMedian(managerData.map(row => row.DPctBudget));

        // Create chart data in the same order as the original: RB, WR, TE, QB, DEF, K
        const positions = [
          { position: 'RB', leagueAvg: rbAvg, manager: manRb },
          { position: 'WR', leagueAvg: wrAvg, manager: manWr },
          { position: 'TE', leagueAvg: teAvg, manager: manTe },
          { position: 'QB', leagueAvg: qbAvg, manager: manQb },
          { position: 'DEF', leagueAvg: defAvg, manager: manDef },
          { position: 'K', leagueAvg: kAvg, manager: manK }
        ];

        setChartData(positions);
        setLoading(false);

      } catch (err) {
        console.error('Error loading draft tendencies data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (selectedManager && draftData.length > 0) {
      loadAndProcessData();
    } else if (selectedManager) {
      setError('No draft data available');
      setLoading(false);
    }
  }, [selectedManager, draftData]);

  // Mobile-optimized tooltip formatter
  const formatTooltip = (value, name, props) => {
    const percentage = (value * 100).toFixed(1);
    
    let label;
    if (name === 'leagueAvg') {
      label = window.innerWidth < 640 ? 'League' : 'League Average';
    } else if (name === 'manager') {
      label = selectedManager;
    } else {
      label = name;
    }
    
    return [`${percentage}%`, label];
  };

  const formatYAxis = (value) => {
    return (value * 100).toFixed(0) + '%';
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
            <span className="hidden sm:inline">
              {selectedManager}'s draft tendencies compared to the League
            </span>
            <span className="sm:hidden">
              {selectedManager}'s Draft Tendencies
            </span>
          </h3>
          <p className="text-gray-600 text-sm">
            <span className="hidden sm:inline">Percentage of budget allocated by position</span>
            <span className="sm:hidden">Budget allocation by position</span>
          </p>
        </div>
        
        <div className="flex items-center justify-center h-64 sm:h-80 lg:h-96">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin"></div>
            <span className="text-gray-600 font-medium text-sm sm:text-base">Loading draft tendencies...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-red-500 py-12">
          <div className="text-sm sm:text-base">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-gray-500 py-12">
          <div className="text-sm sm:text-base">No draft tendency data found for {selectedManager}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
      {/* Title with mobile optimization */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
          <span className="hidden sm:inline">
            {selectedManager}'s draft tendencies compared to the League
          </span>
          <span className="sm:hidden">
            {selectedManager}'s Draft Tendencies
          </span>
        </h3>
        <p className="text-gray-600 text-sm">
          <span className="hidden sm:inline">Percentage of budget allocated by position</span>
          <span className="sm:hidden">Budget allocation by position</span>
        </p>
      </div>

      {/* Chart with responsive height */}
      <div className="h-64 sm:h-80 lg:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData}
            margin={{ 
              top: 10, 
              right: window.innerWidth < 640 ? 10 : 30, 
              left: window.innerWidth < 640 ? 10 : 20, 
              bottom: window.innerWidth < 640 ? 40 : 20 
            }}
            barCategoryGap={window.innerWidth < 640 ? "15%" : "20%"}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#f0f0f0" 
              vertical={false}
            />
            <XAxis 
              dataKey="position" 
              tick={{ 
                fontSize: window.innerWidth < 640 ? 11 : 14, 
                fill: '#374151' 
              }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              height={window.innerWidth < 640 ? 30 : 40}
            />
            <YAxis 
              tickFormatter={formatYAxis}
              tick={{ 
                fontSize: window.innerWidth < 640 ? 10 : 12, 
                fill: '#6b7280' 
              }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              domain={[0, 'dataMax']}
              width={window.innerWidth < 640 ? 35 : 50}
            />
            <Tooltip 
              formatter={formatTooltip}
              labelStyle={{ 
                color: '#374151', 
                fontWeight: 'bold',
                fontSize: window.innerWidth < 640 ? '12px' : '14px'
              }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: window.innerWidth < 640 ? '6px' : '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                fontSize: window.innerWidth < 640 ? '12px' : '14px',
                padding: window.innerWidth < 640 ? '8px' : '12px'
              }}
            />
            
            {/* Conditional Legend - hide on very small screens */}
            {window.innerWidth >= 640 && (
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
                fontSize={12}
              />
            )}
            
            <Bar 
              dataKey="leagueAvg" 
              name={window.innerWidth < 640 ? "League" : "League Average"}
              fill="#9ca3af"
              radius={[2, 2, 0, 0]}
              maxBarSize={window.innerWidth < 640 ? 30 : 50}
            />
            <Bar 
              dataKey="manager" 
              name={selectedManager}
              fill="#8b5cf6"
              radius={[2, 2, 0, 0]}
              maxBarSize={window.innerWidth < 640 ? 30 : 50}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Mobile Legend (when hidden from chart) */}
      {window.innerWidth < 640 && (
        <div className="mt-3 flex items-center justify-center space-x-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-2 bg-gray-400"></div>
            <span className="text-gray-700">League</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-2 bg-purple-600"></div>
            <span className="text-gray-700">{selectedManager}</span>
          </div>
        </div>
      )}

      {/* Additional Stats with responsive grid */}
      <div className="mt-4 sm:mt-6 grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        {chartData.map((item) => {
          const diff = item.manager - item.leagueAvg;
          const diffPercentage = (diff * 100).toFixed(1);
          const isPositive = diff > 0;
          
          return (
            <div key={item.position} className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
              <div className="text-xs sm:text-sm font-semibold text-gray-700">{item.position}</div>
              <div className={`text-xs mt-1 font-medium ${
                isPositive ? 'text-green-600' : 
                diff < 0 ? 'text-red-600' : 'text-gray-500'
              }`}>
                {diff === 0 ? 'Even' : `${isPositive ? '+' : ''}${diffPercentage}%`}
              </div>
              
              {/* Mobile: Show actual percentages */}
              <div className="sm:hidden text-xs text-gray-500 mt-1">
                <div>{(item.manager * 100).toFixed(0)}%</div>
                <div className="text-gray-400">({(item.leagueAvg * 100).toFixed(0)}%)</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile explanation */}
      <div className="sm:hidden mt-3 text-xs text-gray-500 text-center">
        <p>Numbers show difference from league average</p>
        <p>Your % (League %) shown below each position</p>
      </div>
    </div>
  );
};

export default DraftTendenciesChart;